import CustomBarcode from '@components/CustomBarcode';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usedStamp from '@icons/gift/ic_gift_used.svg';
import RefreshIcon from '@icons/Refresh';
import { GiftBoxResponse, GiftCouponStatus } from '@models/gift';
import { pressActionEffect } from '@styles/mixin';
import { useTranslation } from 'react-i18next';

import useBarcodeTimer from '../../hooks/useBarcodeTimer';
import { getReceivedCouponStatus } from '../../utils';

interface Props {
    giftBox: GiftBoxResponse;
}

function GiftCard({ giftBox }: Props) {
    const { t } = useTranslation('gift');
    const coupon = giftBox.giftCoupon;
    const card = giftBox.giftCard;
    const name = coupon?.name || card?.name || '';
    const imageUrl = coupon?.imageUrl || card?.imageUrl || '';
    const status = getReceivedCouponStatus(giftBox);
    const isUsed = status === GiftCouponStatus.USED;
    const couponNo = coupon?.couponNumber?.replace(/\s/g, '') || '';
    const showBarcode = (status === GiftCouponStatus.ISSUED || isUsed) && !!couponNo;

    const formattedNo = couponNo.match(/.{1,4}/g)?.join(' ') || couponNo;

    return (
        <Card>
            <ImageWrap>
                <ProductImage src={imageUrl} width={200} height={200} alt={name} />
                {isUsed && (
                    <Stamp src={usedStamp} width={84} height={84} alt={t('giftReceived.status.used')} />
                )}
            </ImageWrap>
            <Text size='title' fontWeight='bold' align='center'>{name}</Text>
            {showBarcode && (
                <BarcodeBlock>
                    <CustomBarcode value={couponNo} height={40} />
                    <BarcodeNo as='span' size='body-long'>{formattedNo}</BarcodeNo>
                </BarcodeBlock>
            )}
        </Card>
    );
}

const Card = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-4);
    padding: var(--spacing-6);
    border-radius: 8px;
    background: ${colors.white};
`;

const ImageWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 200px;
    height: 200px;
`;

const ProductImage = styled(Image)`
    border-radius: 8px;
    object-fit: contain;
`;

const Stamp = styled.img`
    position: absolute;
    top: 16px;
    right: -10%;
`;

const BarcodeBlock = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-2);
    padding-top: var(--spacing-2);
`;

const BarcodeNo = styled(Text)`
    letter-spacing: 0.04em;
`;

const TimerRow = styled.button`
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-1);
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: var(--spacing-1);
`;
export default GiftCard;
